import { useState, useEffect } from 'react';
import { Clock, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { ReturnProcessing } from '../components/ReturnProcessing';

export function ReturnsDashboard() {
  const { user } = useAuth();
  const [returnsHistory, setReturnsHistory] = useState<any[]>([]);

  // Search & Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState<'ALL' | 'TOKEN' | 'CASH'>('ALL');
  
  // Pagination & limit
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);

  const fetchReturns = async () => {
    if (!user?.tenantId) return;
    try {
      const headers = { 'x-tenant-id': user.tenantId };
      const res = await api.get('/returns', { headers });
      setReturnsHistory(res.data.data || []);
    } catch (err) {
      console.error('Failed to retrieve return logs.');
    }
  };

  useEffect(() => {
    fetchReturns();
  }, [user?.tenantId]);

  // Filter returns
  const filteredReturns = returnsHistory.filter((ret) => {
    const term = searchTerm.toLowerCase().trim();
    const matchesSearch = 
      (ret.returnNumber || '').toLowerCase().includes(term) ||
      (ret.invoiceNumber || '').toLowerCase().includes(term) ||
      (ret.invoiceId || '').toLowerCase().includes(term) ||
      (ret.tokenCode || '').toLowerCase().includes(term) ||
      (ret.customerName || '').toLowerCase().includes(term) ||
      (ret.notes || '').toLowerCase().includes(term);

    const isCash = ret.tokenCode?.startsWith('CSH-') || false;
    let matchesMode = true;
    if (filterMode === 'TOKEN') {
      matchesMode = !isCash;
    } else if (filterMode === 'CASH') {
      matchesMode = isCash;
    }

    return matchesSearch && matchesMode;
  });

  // Paginated returns
  const totalPages = Math.ceil(filteredReturns.length / limit);
  const startIndex = (page - 1) * limit;
  const paginatedReturns = filteredReturns.slice(startIndex, startIndex + limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-surface-900">Returns & Exchanges</h1>
        <p className="mt-1 text-sm text-surface-500 font-light">
          Process merchant returns, reverse inventory logs, and write off damaged goods.
        </p>
      </div>

      {/* Primary Return Processing Workspace */}
      <ReturnProcessing
        onSuccess={fetchReturns}
        onCancel={() => { }}
      />

      {/* Historical returns log */}
      <div className="card space-y-4 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-3">
          <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
            <Clock size={16} className="text-emerald-600" />
            <span>Historical Refund Logs</span>
          </h2>

          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-slate-550">Show:</label>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              className="bg-white border border-slate-250 rounded px-2 py-1 text-xs font-bold text-slate-700 focus:outline-none focus:border-emerald-600 cursor-pointer shadow-sm"
            >
              <option value={10}>10 rows</option>
              <option value={20}>20 rows</option>
              <option value={30}>30 rows</option>
            </select>
          </div>
        </div>

        {/* Filter controls bar */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search bar */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-405" />
            <input
              type="text"
              placeholder="Search by Return ID, Invoice, Token Code, or Customer..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="block w-full pl-9 pr-3 py-1.5 border border-slate-200 rounded-lg bg-slate-50 text-xs focus:outline-none focus:bg-white focus:border-emerald-600 transition-all font-medium text-slate-700 shadow-sm"
            />
          </div>

          {/* Refund type filter */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => { setFilterMode('ALL'); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border cursor-pointer ${
                filterMode === 'ALL'
                  ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm font-extrabold'
                  : 'bg-white text-slate-650 border-slate-200 hover:bg-slate-50'
              }`}
            >
              All Types
            </button>
            <button
              onClick={() => { setFilterMode('TOKEN'); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border cursor-pointer ${
                filterMode === 'TOKEN'
                  ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm font-extrabold'
                  : 'bg-white text-slate-650 border-slate-200 hover:bg-slate-50'
              }`}
            >
              Tokens Only
            </button>
            <button
              onClick={() => { setFilterMode('CASH'); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border cursor-pointer ${
                filterMode === 'CASH'
                  ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm font-extrabold'
                  : 'bg-white text-slate-650 border-slate-200 hover:bg-slate-50'
              }`}
            >
              Cash Only
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-100 text-slate-400 font-bold uppercase tracking-wider bg-slate-50/50">
                <th className="py-2.5 px-3 text-center">S.No.</th>
                <th className="py-2.5 px-3">Return ID</th>
                <th className="py-2.5 px-2">Invoice Ref</th>
                <th className="py-2.5 px-2">Token Code</th>
                <th className="py-2.5 px-2">Date & Time</th>
                <th className="py-2.5 px-2">Customer Details</th>
                <th className="py-2.5 px-2">Reason Label</th>
                <th className="py-2.5 px-3 text-right">Refund Value (₹)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedReturns.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-slate-400 font-medium">
                    No matching return logs found.
                  </td>
                </tr>
              ) : (
                paginatedReturns.map((ret: any, idx) => {
                  const actualIdx = startIndex + idx;
                  return (
                    <tr key={ret.id} className="hover:bg-slate-50/50">
                      <td className="py-2.5 px-3 text-center font-bold text-slate-500">{actualIdx + 1}</td>
                      <td className="py-2.5 px-3 font-mono font-bold text-red-600">{ret.returnNumber || ret.id}</td>
                      <td className="py-2.5 px-2 font-mono font-semibold text-slate-600">{ret.invoiceNumber || ret.invoiceId}</td>
                      <td className="py-2.5 px-2 font-mono font-bold text-emerald-700">{ret.tokenCode || 'N/A'}</td>
                      <td className="py-2.5 px-2 text-slate-500">{new Date(ret.createdAt || Date.now()).toLocaleString()}</td>
                      <td className="py-2.5 px-2 font-semibold text-slate-800">{ret.customerName}</td>
                      <td className="py-2.5 px-2 text-slate-500 truncate max-w-[150px]">{ret.notes || 'Not specified'}</td>
                      <td className="py-2.5 px-3 text-right font-mono font-bold text-slate-900">₹{(ret.totalRefundAmount ?? 0).toFixed(2)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-slate-100 pt-4">
            <span className="text-xs text-slate-500">
              Showing page <span className="font-semibold text-slate-700">{page}</span> of{' '}
              <span className="font-semibold text-slate-700">{totalPages}</span> ({filteredReturns.length} filtered records)
            </span>
            <div className="flex items-center gap-1.5">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="p-1.5 border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-50 cursor-pointer text-slate-600 bg-white"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="p-1.5 border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-50 cursor-pointer text-slate-600 bg-white"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
